import React, { useState, useEffect, useCallback } from 'react';
const API_URL = process.env.REACT_APP_API_URL || 'https://stock-api-production-913a.up.railway.app';
import { getRanking } from '../api';

// [5D_SORT_PATCHED]
const SORT_FIELDS = [
  { key: 'rev_yoy', label: '月營收年增率' },
  { key: 'gross_rate_chg', label: '毛利率季增' },
  { key: 'op_rate_chg', label: '營益率季增' },
  { key: 'op_income_yoy', label: '營業利益年增率' },
  { key: 'price_change_5d', label: '5天漲跌' },
];

export default function MarketRanking({ setTicker }) {
  // 手機偵測
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [watchlist, setWatchlist] = useState([]);

  // [WL_ROLE_PATCHED] 管理員看後端 DB, 訪客看自己的 localStorage
  const loadWatchlist = useCallback(() => {
    const _token = sessionStorage.getItem('wintime_admin_token') || '';
    const isAdmin = !!_token;
    if (isAdmin) {
      // 5 秒 cache:避免重複呼叫(11 次 → 1 次)
      const now = Date.now();
      if (window.__wlCache && (now - window.__wlCacheTime < 5000)) {
        setWatchlist(window.__wlCache);
        return;
      }
      fetch(`${API_URL}/api/watchlist`).then(r=>r.json()).then(d=>{
        const list = Array.isArray(d) ? d : [];
        window.__wlCache = list;
        window.__wlCacheTime = Date.now();
        setWatchlist(list);
      }).catch(()=>{});
    } else {
      try {
        const stored = localStorage.getItem('wt_watchlist');
        setWatchlist(stored ? JSON.parse(stored) : []);
      } catch { setWatchlist([]); }
    }
  }, []);

  useEffect(() => {
    loadWatchlist();
    window.addEventListener('watchlistChanged', loadWatchlist);
    return () => window.removeEventListener('watchlistChanged', loadWatchlist);
  }, [loadWatchlist]);

  const isInWatchlist = t => watchlist.some(w => w.ticker === t);

  const toggleWatchlist = (ticker, name, inWL) => {
    const _token = sessionStorage.getItem('wintime_admin_token') || '';
    const isAdmin = !!_token;
    if (isAdmin) {
      // 管理員: 寫後端 DB
      if (inWL) {
        setWatchlist(prev => prev.filter(w => w.ticker !== ticker));
        fetch(`${API_URL}/api/watchlist/${ticker}?token=${encodeURIComponent(_token)}`, { method:'DELETE' })
          .then(() => window.dispatchEvent(new CustomEvent('watchlistChanged', { detail:{ action:'remove', ticker }})))
          .catch(() => loadWatchlist());
      } else {
        setWatchlist(prev => [...prev, { ticker, name }]);
        fetch(`${API_URL}/api/watchlist/${ticker}?name=${encodeURIComponent(name)}&token=${encodeURIComponent(_token)}`, { method:'POST' })
          .then(r=>r.json()).then(d=>{ if(!d.ok) loadWatchlist(); else window.dispatchEvent(new CustomEvent('watchlistChanged', { detail:{ action:'add', ticker, name }})); })
          .catch(() => loadWatchlist());
      }
    } else {
      // 訪客: 寫 localStorage (與 Sidebar 共用 wt_watchlist key)
      const next = inWL
        ? watchlist.filter(w => w.ticker !== ticker)
        : [...watchlist, { ticker, name }];
      setWatchlist(next);
      try { localStorage.setItem('wt_watchlist', JSON.stringify(next)); } catch {}
      window.dispatchEvent(new CustomEvent('watchlistChanged', { detail:{ action: inWL ? 'remove' : 'add', ticker, name }}));
    }
  };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('rev_yoy');
  const [sortDir, setSortDir] = useState('desc');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [displayCount, setDisplayCount] = useState(100);
  const [latestMonthOnly, setLatestMonthOnly] = useState(false);

  useEffect(() => {
    getRanking().then(rows => {
      const processed = rows.map(s => ({
        ...s,
        op_income_yoy: (s.op_income != null && s.prev_year_op_income != null && Number(s.prev_year_op_income) !== 0)
          ? (Number(s.op_income) - Number(s.prev_year_op_income)) / Math.abs(Number(s.prev_year_op_income)) * 100 : null,
        cur_rev_b: s.cur_rev != null ? (Number(s.cur_rev) / 100000).toFixed(2) : null,
        prev_rev_b: s.prev_rev != null ? (Number(s.prev_rev) / 100000).toFixed(2) : null,
      }));
      setData(processed);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="loading">⏳ 載入全市場資料...</div>;

  const allMs = data.map(r => r.lm).filter(Boolean);
  const latestM = allMs.sort().reverse()[0];

  let filtered = data.filter(r => {
    if (r.cur_rev == null || r.prev_rev == null) return false;
    return Number(r.cur_rev) >= 30000 && Number(r.prev_rev) >= 30000;
  });
  if (latestMonthOnly) {
    filtered = filtered.filter(r => r.lm === latestM);
  }
  if (filterIndustry.trim()) {
    const kw = filterIndustry.trim();
    filtered = filtered.filter(r =>
      (r.new_category || '').includes(kw) ||
      (r.sub_industry || '').includes(kw) ||
      (r.name || '').includes(kw)
    );
  }

  filtered = [...filtered].sort((a, b) => {
    const va = a[sortField] ?? -Infinity;
    const vb = b[sortField] ?? -Infinity;
    return sortDir === 'desc' ? vb - va : va - vb;
  });

  const displayed = filtered.slice(0, displayCount);

  const fmt = (v, dec=2) => v == null || v === '' || isNaN(Number(v)) ? '-' : Number(v).toFixed(dec) + '%';
  const fmtChg = v => v == null || isNaN(v) ? '-' : (v > 0 ? '+' : '') + Number(v).toFixed(2);
  const fmtN = v => v == null || isNaN(v) ? '-' : (v > 0 ? '+' : '') + Number(v).toLocaleString();
  const colorVal = v => (v == null || isNaN(v)) ? '#aaa' : v > 0 ? '#d62828' : v < 0 ? '#00a14b' : '#aaa';
  const colorValInv = v => (v == null || isNaN(v)) ? '#aaa' : v > 0 ? '#00a14b' : v < 0 ? '#d62828' : '#aaa';  // 散戶反指標:增加綠/減少紅

  // 表格樣式(手機縮 padding)
  const th = { padding: isMobile ? '6px 6px' : '8px 10px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #2a3a4a', position: 'sticky', top: 0, background: '#0d1520', zIndex: 10, fontSize: isMobile ? 11 : 12, whiteSpace: 'nowrap' };
  const td = { padding: isMobile ? '5px 6px' : '6px 10px', textAlign: 'right', borderBottom: '1px solid #1e2a3a', fontSize: isMobile ? 11 : 12, whiteSpace: 'nowrap' };

  return (
    <div style={{ padding: isMobile ? '4px' : '8px' }}>
      <h2 style={{ marginBottom: isMobile ? 10 : 16, fontSize: isMobile ? 18 : 22, fontWeight: 700 }}>🏆 全市場排名</h2>

      <div style={{ display: 'flex', gap: isMobile ? 6 : 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {SORT_FIELDS.map(f => (
          <button key={f.key}
            onClick={() => { if (sortField === f.key) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortField(f.key); setSortDir('desc'); } }}
            style={{ padding: isMobile ? '5px 10px' : '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', background: sortField === f.key ? '#4C9BB8' : '#2a3a4a', color: '#fff', fontSize: isMobile ? 12 : 13, fontWeight: sortField === f.key ? 700 : 400 }}>
            {f.label} {sortField === f.key ? (sortDir === 'desc' ? '▼' : '▲') : ''}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="產業/名稱篩選"
          value={filterIndustry}
          onChange={e => setFilterIndustry(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #333', background: '#1e2a3a', color: '#fff', width: isMobile ? '100%' : 200, boxSizing: 'border-box', flex: isMobile ? '1 1 100%' : undefined }}
        />
        <div style={{ display: 'flex', gap: 16 }}>
          {['desc', 'asc'].map(d => (
            <label key={d} style={{ cursor: 'pointer', color: sortDir === d ? '#4C9BB8' : '#aaa', fontSize: 14 }}>
              <input type="radio" checked={sortDir === d} onChange={() => setSortDir(d)} style={{ marginRight: 4 }} />
              {d === 'desc' ? '由高到低' : '由低到高'}
            </label>
          ))}
        </div>
        <select value={displayCount} onChange={e => setDisplayCount(Number(e.target.value))}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #333', background: '#1e2a3a', color: '#fff' }}>
          {[50, 100, 200, 500].map(n => <option key={n} value={n}>顯示 {n} 筆</option>)}
        </select>
        <span style={{ color: '#aaa', fontSize: 13 }}>共 {filtered.length} 筆</span>
        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 12px', borderRadius: 6, fontSize: 13, userSelect: 'none',
          background: latestMonthOnly ? '#2a0d0d' : '#1a2535',
          border: `1px solid ${latestMonthOnly ? '#7a2a2a' : '#2a3a4a'}`,
          color: latestMonthOnly ? '#d62828' : '#7ab0cc' }}>
          <input type="checkbox" checked={latestMonthOnly}
            onChange={e => setLatestMonthOnly(e.target.checked)}
            style={{ cursor: 'pointer' }} />
          只看已公布最新月份營收
        </label>
      </div>

      {/* 手機提示:左右滑可看更多欄位 */}
      {isMobile && (
        <div style={{ fontSize: 11, color: '#5a7a92', marginBottom: 6, textAlign: 'center' }}>
          ← 左右滑動可看更多欄位 →
        </div>
      )}

      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: isMobile ? 'calc(100vh - 280px)' : 'calc(100vh - 200px)', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#0d1520', color: '#ddd' }}>
              <th style={{ ...th, position:'sticky', left:0, zIndex:20, background:'#0d1520', minWidth: isMobile ? 80 : 100, textAlign:'left' }}>代號／名稱</th>
              <th style={{ ...th, textAlign:'left' }}>新分類</th>
              <th style={th}>收盤價</th>
              <th style={th}>5天漲跌(%)</th>
              <th style={th}>大戶持股增減(張)</th>
              <th style={th}>散戶人數增減(人)</th>
              <th style={th}>月營收年增率(%)</th>
              <th style={th}>本月營收(億)</th>
              <th style={th}>去年同月(億)</th>
              <th style={th}>本季毛利率(%)</th>
              <th style={th}>毛利率增減(pp)</th>
              <th style={th}>本季營益率(%)</th>
              <th style={th}>營益率增減(pp)</th>
              <th style={th}>營業利益年增率(%)</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((row, i) => {
              const rowBg = i % 2 === 0 ? '#080d14' : '#0a1018';
              return (
                <tr key={row.ticker} style={{ background: rowBg }}>
                  <td style={{ ...td, textAlign:'left', position:'sticky', left:0, zIndex:1, background: rowBg, fontWeight:600 }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                      <input type="checkbox"
                        checked={isInWatchlist(row.ticker)}
                        onChange={e => { e.stopPropagation(); toggleWatchlist(row.ticker, row.name, isInWatchlist(row.ticker)); }}
                        style={{ cursor: 'pointer', width: 14, height: 14 }}
                      />
                      <span style={{ color:'#4C9BB8', cursor:'pointer', fontWeight:700 }}
                        onClick={() => setTicker && setTicker(row.ticker)}>{row.ticker}</span>
                    </span>
                    <div style={{ color:'#aaa', fontSize:11 }}>{row.name}</div>
                  </td>
                  <td style={{ ...td, color:'#f5c518', fontSize:11, textAlign:'left' }}>{row.new_category || row.sub_industry || '-'}</td>
                  <td style={{ ...td, color:'#fff', fontFamily:"'JetBrains Mono',monospace" }}>
                    {row.close_now ? Number(row.close_now).toLocaleString() : '-'}
                  </td>
                  <td style={{ ...td, color: colorVal(row.price_change_5d) }}>
                    {row.price_change_5d != null ? (row.price_change_5d > 0 ? '+' : '') + row.price_change_5d + '%' : '-'}
                  </td>
                  <td style={{ ...td, color: colorVal(row.big_shares_chg) }}>{fmtN(row.big_shares_chg)}</td>
                  <td style={{ ...td, color: colorValInv(row.retail_holders_chg) }}>{fmtN(row.retail_holders_chg)}</td>
                  <td style={{ ...td, color: colorVal(row.rev_yoy) }}>{fmt(row.rev_yoy)}</td>
                  <td style={{ ...td, color:'#ddd' }}>{row.cur_rev_b ?? '-'}</td>
                  <td style={{ ...td, color:'#ddd' }}>{row.prev_rev_b ?? '-'}</td>
                  <td style={{ ...td, color:'#ccc' }}>{fmt(row.gross_rate)}</td>
                  <td style={{ ...td, color: colorVal(row.gross_rate_chg) }}>{fmtChg(row.gross_rate_chg)}</td>
                  <td style={{ ...td, color:'#ccc' }}>{fmt(row.op_rate)}</td>
                  <td style={{ ...td, color: colorVal(row.op_rate_chg) }}>{fmtChg(row.op_rate_chg)}</td>
                  <td style={{ ...td, color: colorVal(row.op_income_yoy) }}>{fmt(row.op_income_yoy)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
